import { Link } from 'react-router';
import { Alert, PageHeader, datum, eur, useLoad } from '../shared/ui.js';

interface Dashboard {
  activeAccounts: number;
  accountsWithoutCondition: number;
  activeCustomerUsers: number;
  activeArticles: number;
  activePriceListVersion: {
    versionLabel: string;
    validFrom: string;
    itemCount: number;
  } | null;
  quotesIssuedLast30Days: number;
  recentQuotes: Array<{
    quoteId: string;
    quoteNumber: string | null;
    issuedAt: string | null;
    documentTotalNet: string | null;
    currency: string;
    accountName: string;
    accountNumber: string;
  }>;
}

/** Kleines internes Dashboard: Zaehlungen aus vorhandenen Tabellen, keine Umsatzwahrheit. */
export function DashboardPage(): React.JSX.Element {
  const { data, error } = useLoad<Dashboard>('/api/admin/dashboard');
  return (
    <>
      <PageHeader
        title="Dashboard"
        lead="Ueberblick ueber Kunden, Preise und Angebote."
      />
      <Alert text={error} />
      {data !== null && (
        <>
          <div className="kpis">
            <Kpi
              label="Aktive Kunden"
              value={String(data.activeAccounts)}
              to="/admin/kunden"
            />
            <Kpi
              label="Kunden ohne Kondition"
              value={String(data.accountsWithoutCondition)}
              to="/admin/konditionen"
              warn={data.accountsWithoutCondition > 0}
            />
            <Kpi label="Aktive Kundenzugaenge" value={String(data.activeCustomerUsers)} />
            <Kpi
              label="Aktive Artikel"
              value={String(data.activeArticles)}
              to="/admin/artikel"
            />
            <Kpi
              label="Aktive Preisliste"
              value={data.activePriceListVersion?.versionLabel ?? '–'}
              sub={
                data.activePriceListVersion
                  ? `${data.activePriceListVersion.itemCount} Preise, gueltig ab ${datum(data.activePriceListVersion.validFrom)}`
                  : 'keine aktive Version'
              }
              to="/admin/preislisten"
              warn={data.activePriceListVersion === null}
            />
            <Kpi
              label="Angebote, letzte 30 Tage"
              value={String(data.quotesIssuedLast30Days)}
            />
          </div>

          <section className="card">
            <h3>Zuletzt ausgestellte Angebote</h3>
            {data.recentQuotes.length === 0 ? (
              <p className="muted">Noch keine ausgestellten Angebote.</p>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Angebotsnummer</th>
                      <th>Kunde</th>
                      <th>Datum</th>
                      <th className="num">Gesamt netto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentQuotes.map((q) => (
                      <tr key={q.quoteId}>
                        <td>
                          <Link to={`/admin/angebote/${q.quoteId}`}>
                            <code>{q.quoteNumber}</code>
                          </Link>
                        </td>
                        <td>
                          {q.accountName}{' '}
                          <span className="muted">({q.accountNumber})</span>
                        </td>
                        <td>{datum(q.issuedAt)}</td>
                        <td className="num">{eur(q.documentTotalNet)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  sub,
  to,
  warn = false,
}: {
  label: string;
  value: string;
  sub?: string;
  to?: string;
  warn?: boolean;
}): React.JSX.Element {
  const inhalt = (
    <>
      <span className="kpi-label">{label}</span>
      <span className={warn ? 'kpi-value kpi-warn' : 'kpi-value'}>{value}</span>
      {sub !== undefined && <span className="note">{sub}</span>}
    </>
  );
  return to === undefined ? (
    <div className="kpi">{inhalt}</div>
  ) : (
    <Link to={to} className="kpi kpi-link">
      {inhalt}
    </Link>
  );
}
